<template>
  <div class="w-full p-4 mt-4 rounded-lg border-[1px] border-gray-200 shadow-sm bg-white">
    <div class="text-sm flex justify-between items-center">
      <span>
        Internal Comments
        <span
          class="w-6 h-6 inline-flex items-center justify-center rounded-full ml-1"
          :class="{ 'bg-yellow-200': comments.length > 0, 'bg-gray-200': comments.length === 0 }"
        >{{ comments.length || 0 }}</span>
      </span>
      <button @click="toggleAddComment" class="text-sm text-cosmicPurple hover:text-cosmicPurple/80 transition-all duration-300 flex items-center gap-1">
        <Plus
class="w-4 h-4 transition-transform duration-300" 
        :class="{ 'rotate-45': isAddingComment }"
        />
        Add Comment
      </button>
    </div>

    <div v-if="isAddingComment" class="my-6">
      <textarea
        v-model="newComment"
        placeholder="Add a comment"
        class="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cosmicPurple/50 focus:border-cosmicPurple overflow-hidden"
        rows="3"
        @input="autoResize($event)"
      />
      <button @click="saveComment" class="mt-2 bg-black text-white px-4 py-2 rounded-md text-xs transition-all duration-300" :disabled="isSavingComment">
        <span v-if="isSavingComment">
          <Loader2 class="animate-spin h-4 w-4 inline mr-1 text-white" />
          Submitting...
        </span>
        <span v-else>Submit comment</span>
      </button>
    </div>

    <div v-if="isFetchingComments" class="flex justify-center items-center py-8">
      <Loader2 class="animate-spin h-5 w-5 text-gray-400" />
    </div>
    <div v-else-if="comments.length > 0" class="mt-2 flex flex-col gap-4">
      <div
        v-for="comment in comments"
        :key="comment.id"
        class="ring-[1px] ring-gray-300/50 p-4 shadow-sm rounded-md bg-white hover:shadow-md transition-all duration-300 relative"
      >
        <div class="flex items-center text-sm">
          <img
            v-if="comment.created_by.avatar_image"
            :src="comment.created_by.avatar_image"
            :alt="comment.created_by.first_name"
            class="h-8 w-8 object-cover rounded-full"
          />
          <span
            v-else
            class="h-8 w-8 bg-gray-200 flex justify-center items-center rounded-full"
          >
            <UserRound class="w-4 h-4 text-black" />
          </span>
          <div class="ml-2">
            <p v-if="comment.created_by.username === 'admin'" class="font-semibold">
              Legacy Airtable comment
            </p>
            <p v-else class="font-semibold">
              {{ comment.created_by.first_name }} {{ comment.created_by.last_name }}
            </p>
            <p class="text-gray-500">{{ formatDate(comment.created_at) }}</p>
          </div>
          <!-- EllipsisVertical menu -->
          <div class="ml-auto relative">
            <button @click="popoutOpenId = popoutOpenId === comment.id ? null : comment.id" class="p-1 rounded-full hover:bg-gray-100">
              <EllipsisVertical class="w-5 h-5 text-gray-400" />
            </button>
            <div v-if="popoutOpenId === comment.id" class="absolute right-0 mt-2 w-32 bg-white border rounded shadow z-10">
              <button
                v-if="currentUser && comment.created_by.username === currentUser.username"
                @click="startEditComment(comment)"
                class="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                :disabled="isEditingCommentLoading"
              >
                <span v-if="isEditingCommentLoading && editingCommentId === comment.id">
                  <Loader2 class="animate-spin h-3 w-3 inline mr-1 text-gray-400" />
                  Saving...
                </span>
                <span v-else>Edit</span>
              </button>
              <button
                @click="deleteComment(comment)"
                class="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600"
                :disabled="isDeletingCommentId === comment.id"
              >
                <span v-if="isDeletingCommentId === comment.id">
                  <Loader2 class="animate-spin h-3 w-3 inline mr-1 text-gray-400" />
                  Deleting...
                </span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
        <div v-if="editingCommentId === comment.id" class="mt-2">
          <textarea
            ref="editingTextareaRef"
            v-model="editingCommentText"
            class="w-full p-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cosmicPurple/50 focus:border-cosmicPurple overflow-hidden resize-none"
            :style="{ minHeight: '60px' }"
            @input="autoResize($event)"
          />
          <div class="flex gap-2 mt-2">
            <button @click="submitEditComment(comment)" class="bg-black text-white px-4 py-2 rounded text-xs" :disabled="isEditingCommentLoading">
              <span v-if="isEditingCommentLoading">
                <Loader2 class="animate-spin h-3 w-3 inline mr-1 text-white" />
                Saving...
              </span>
              <span v-else>Save</span>
            </button>
            <button @click="cancelEditComment" class="bg-gray-200 text-gray-700 px-4 py-2 rounded text-xs" :disabled="isEditingCommentLoading">Cancel</button>
          </div>
        </div>
        <div v-else class="text-sm mt-2 whitespace-pre-wrap">
          {{ comment.comment }}
        </div>
      </div>
    </div>
    <div v-else class="text-center text-xs text-gray-500 py-4">No comments yet</div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { Plus, Loader2, UserRound, EllipsisVertical } from 'lucide-vue-next'
import { formatDate } from '@/utils/formatDate'
import { useUserStore } from '@/stores/user'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

interface Comment {
  id: string | number
  comment: string
  created_at: string
  created_by: {
    username: string
    first_name: string
    last_name: string
    avatar_image?: string
  }
}

const props = defineProps<{
  comments: Comment[]
  username: string
  isFetchingComments?: boolean
}>()

const emit = defineEmits<{
  (e: 'comments-updated'): void
}>()

const userStore = useUserStore()
const currentUser = userStore.currentUser

const isAddingComment = ref(false)
const newComment = ref('')
const isSavingComment = ref(false)
const isEditingCommentLoading = ref(false)
const isDeletingCommentId = ref<string | number | null>(null)
const editingCommentId = ref<string | number | null>(null)
const editingCommentText = ref('')
const popoutOpenId = ref<string | number | null>(null)
const editingTextareaRef = ref<HTMLTextAreaElement | HTMLTextAreaElement[] | null>(null)

const toggleAddComment = () => {
  isAddingComment.value = !isAddingComment.value
}

const autoResize = (event: Event) => {
  const textarea = event.target as HTMLTextAreaElement
  textarea.style.height = 'auto'
  textarea.style.height = textarea.scrollHeight + 'px'
}


const saveComment = async () => {
  if (!newComment.value.trim()) return
  isSavingComment.value = true
  try {
    await api.post(API_ROUTES.CREATE_INTERNAL_COMMENT, {
      username: props.username,
      comment: newComment.value.trim(),
    })
    newComment.value = ''
    isAddingComment.value = false
    emit('comments-updated')
  } catch (err) {
    console.error('Failed to submit comment:', err)
  } finally {
    isSavingComment.value = false
  }
}

const startEditComment = async (comment: Comment) => {
  editingCommentId.value = comment.id
  editingCommentText.value = comment.comment
  popoutOpenId.value = null
  await nextTick()
  // Recalculate height after content is set
  // Recalculate height after content is set
  const textarea = Array.isArray(editingTextareaRef.value) 
    ? editingTextareaRef.value[0] 
    : editingTextareaRef.value as HTMLTextAreaElement | null

  if (textarea) {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }
}

const cancelEditComment = () => {
  editingCommentId.value = null
  editingCommentText.value = ''
  editingTextareaRef.value = null
}

const submitEditComment = async (comment: Comment) => {
  if (!editingCommentText.value.trim()) return
  isEditingCommentLoading.value = true
  try {
    await api.patch(API_ROUTES.EDIT_INTERNAL_COMMENT(String(comment.id)), {
      comment: editingCommentText.value.trim(),
    })
    editingCommentId.value = null
    editingCommentText.value = ''
    emit('comments-updated')
  } catch (err) {
    console.error('Failed to edit comment:', err)
  } finally {
    isEditingCommentLoading.value = false
  }
}

const deleteComment = async (comment: Comment) => {
  isDeletingCommentId.value = comment.id
  try {
    await api.patch(API_ROUTES.EDIT_INTERNAL_COMMENT(String(comment.id)), {
      is_active: false,
    })
    popoutOpenId.value = null
    emit('comments-updated')
  } catch (err) {
    console.error('Failed to delete comment:', err)
  } finally {
    isDeletingCommentId.value = null
  }
}
</script>